import {
  Button,
  ListItem,
  ListItemButton,
  ListItemText,
  TablePagination,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";

import {
  CampaignStatus,
  EnqueueTexterSendMutation,
  EnqueueTexterSendMutationVariables,
  InboxType,
  RequestAssignmentBatchMutation,
  RequestAssignmentBatchMutationVariables,
  TexterCampaignSendStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  ThreadListItems,
  ThreadListWrapper,
} from "~frontend/components/threads";
import {
  LIST_ASSIGNMENTS,
  REQUEST_ASSIGNMENT_BATCH,
} from "~frontend/graphql/assignments";
import { ENQUEUE_TEXTER_SEND } from "~frontend/graphql/campaigns";
import { GET_INBOX } from "~frontend/graphql/inboxes";
import { useIntl } from "~frontend/providers/IntlProvider";
import {
  CampaignAssignmentRow,
  NonCampaignAssignmentRow,
} from "~frontend/routes/subaccounts/inboxes/AssignmentRow";
import useMutation from "~frontend/util/useMutation";

import { AssignmentSummary, InboxDetails } from "./types";

export function AssignmentList({
  inbox,
  headerText,
  assignments,
  totalCount,
  page,
  pageSize,
  onChangePage,
  onChangePageSize,
  selectedAssignmentId,
  onChangeSelectedAssignmentId,
  loadingAssignmentIds,
}: {
  inbox: InboxDetails;
  headerText?: string;
  assignments: AssignmentSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  onChangePage: (newPage: number) => void;
  onChangePageSize: (newPageSize: number) => void;
  selectedAssignmentId: string | null;
  onChangeSelectedAssignmentId: (id: string) => void;
  loadingAssignmentIds: string[];
}): ReactElement {
  const snackbar = useSnackbar();
  const intl = useIntl();

  const [requestAssignmentBatch, { loading: requestAssignmentBatchLoading }] =
    useMutation<
      RequestAssignmentBatchMutation,
      RequestAssignmentBatchMutationVariables
    >(REQUEST_ASSIGNMENT_BATCH, {
      refetchQueries: [GET_INBOX, LIST_ASSIGNMENTS],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error requesting texts",
        description: "Error message for requesting texts",
      }),
    });

  const handleRequestAssignmentBatch = useCallback(async () => {
    if (requestAssignmentBatchLoading) {
      return;
    }

    const requestAssignmentBatchResponse = await requestAssignmentBatch({
      variables: { input: { inboxId: inbox.id } },
    });

    if (
      requestAssignmentBatchResponse.errors ||
      !requestAssignmentBatchResponse.data
    ) {
      console.error(requestAssignmentBatchResponse.errors);
      snackbar.enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "Error claiming replies",
          description: "Error message for claiming replies",
        })
      );
    }
  }, [
    requestAssignmentBatchLoading,
    requestAssignmentBatch,
    inbox,
    snackbar,
    intl,
  ]);

  const hasPendingTexterSend =
    inbox.__typename === "CampaignInboxSummary" &&
    inbox.campaign.myCampaignSends.some(
      send => send.status === TexterCampaignSendStatus.Pending
    );

  const [enqueueTexterSend, { loading: enqueueTexterSendLoading }] =
    useMutation<EnqueueTexterSendMutation, EnqueueTexterSendMutationVariables>(
      ENQUEUE_TEXTER_SEND,
      {
        refetchQueries: [GET_INBOX, LIST_ASSIGNMENTS],
        awaitRefetchQueries: true,
        onErrorSnackbarMessage: intl.formatMessage({
          defaultMessage: "Error sending initials",
          description: "Error message for sending initial messages",
        }),
      }
    );

  const handleEnqueueTexterSend = useCallback(async () => {
    if (
      enqueueTexterSendLoading ||
      hasPendingTexterSend ||
      inbox.__typename !== "CampaignInboxSummary"
    ) {
      return;
    }

    const { data, errors } = await enqueueTexterSend({
      variables: { input: { campaignId: inbox.campaign.id } },
    });

    if (errors || !data) {
      snackbar.enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "Error sending initials",
          description: "Error message for sending initial messages",
        })
      );
    }
  }, [
    enqueueTexterSendLoading,
    hasPendingTexterSend,
    inbox,
    enqueueTexterSend,
    snackbar,
    intl,
  ]);

  const assignmentListRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (selectedAssignmentId && assignmentListRef.current) {
      assignmentListRef.current
        ?.querySelector(`[data-assignment-id="${selectedAssignmentId}"]`)
        ?.scrollIntoView({ block: "center" });
    }
  }, [selectedAssignmentId]);

  const headerTextDisplay = (() => {
    const inboxName = headerText || inbox.inboxName;
    const totalAssignments = totalCount;
    switch (inbox.inboxType) {
      case "GENERAL":
        return (
          <FormattedMessage
            defaultMessage="{totalAssignments} claimed {totalAssignments, plural, one {text} other {texts}} from {inboxName}"
            description="Total assignment copy in general inbox"
            values={{
              totalAssignments,
              inboxName,
            }}
          />
        );
      case "CAMPAIGN":
        return (
          <FormattedMessage
            defaultMessage="{totalAssignments} claimed {totalAssignments, plural, one {text} other {texts}} from {inboxName}"
            description="Total assignment copy in campaign inbox"
            values={{
              totalAssignments,
              inboxName,
            }}
          />
        );
      case "ESCALATION":
        return (
          <FormattedMessage
            defaultMessage="{totalAssignments} claimed {totalAssignments, plural, one {text} other {texts}} from {inboxName}"
            description="Total assignment copy in escalation inbox"
            values={{
              totalAssignments,
              inboxName,
            }}
          />
        );
    }
  })();

  return (
    <ThreadListWrapper sx={{ paddingTop: 0 }}>
      <ThreadListItems ref={assignmentListRef}>
        <ListItem
          sx={{
            fontSize: "12px",
          }}
          divider
          data-testid="assignment-list-header"
        >
          {headerTextDisplay}
        </ListItem>
        {assignments.map(assignment => (
          <ListItemButton
            key={assignment.id}
            data-assignment-id={assignment.id}
            selected={assignment.id === selectedAssignmentId}
            onClick={() => onChangeSelectedAssignmentId(assignment.id)}
            data-testId="assignment-item-btn"
            sx={{
              paddingTop: 0,
              paddingBottom: 0,
            }}
            divider
          >
            <ListItemText
              sx={{ marginBottom: 1 }}
              primary={
                <>
                  {inbox.inboxType !== InboxType.Campaign && (
                    <NonCampaignAssignmentRow
                      assignment={assignment}
                      inbox={inbox}
                      loading={loadingAssignmentIds.includes(assignment.id)}
                    />
                  )}
                  {inbox.inboxType === InboxType.Campaign && (
                    <CampaignAssignmentRow
                      assignment={assignment}
                      loading={loadingAssignmentIds.includes(assignment.id)}
                    />
                  )}
                </>
              }
            />
          </ListItemButton>
        ))}
        <TablePagination
          labelDisplayedRows={({ from, to, count }) => (
            <FormattedMessage
              defaultMessage="{from}-{to} of {totalCount}"
              description="Pagination label"
              values={{
                from,
                to,
                totalCount: count.toLocaleString(),
              }}
            />
          )}
          labelRowsPerPage={
            <FormattedMessage
              defaultMessage="Per page:"
              description="Rows per page label"
            />
          }
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_evt, newPage) => onChangePage(newPage)}
          onRowsPerPageChange={evt =>
            onChangePageSize(parseInt(evt.target.value, 10))
          }
          sx={{ margin: 0 }}
        />
        <FlexBox direction="column" alignItems="center" gap={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleRequestAssignmentBatch}
            disabled={
              requestAssignmentBatchLoading || !inbox.stats.numRepliesAvailable
            }
            data-testid="request-more-replies-btn"
          >
            <FormattedMessage
              defaultMessage="Request More Replies"
              description="Button label for requesting more texts"
            />
          </Button>
          {!inbox.stats.numRepliesAvailable ? (
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="No more available replies!"
                description="Message for when no replies are available to request"
              />
            </Typography>
          ) : (
            <></>
          )}

          {inbox.__typename === "CampaignInboxSummary" &&
            inbox.campaign.texterSendEnabled &&
            inbox.campaign.withinTextingHours &&
            inbox.campaign.status === CampaignStatus.InitialSendRunning && (
              <Button
                variant="contained"
                fullWidth
                color="secondary"
                disabled={enqueueTexterSendLoading || hasPendingTexterSend}
                onClick={handleEnqueueTexterSend}
              >
                {enqueueTexterSendLoading || hasPendingTexterSend ? (
                  <FormattedMessage
                    defaultMessage="Sending Initials..."
                    description="Message for when a texter campaign send is in progress"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Send Initials"
                    description="Button text for sending a batch of initial messages"
                  />
                )}
              </Button>
            )}
        </FlexBox>
      </ThreadListItems>
    </ThreadListWrapper>
  );
}
